import { Injectable } from '@angular/core';

/**
 * Query Builder Service
 * Transforms query object from CustomBackendService into custom querystring format
 * Supports multiple filter types: string, number range, date, etc.
 */
@Injectable({ providedIn: 'root' })
export class QueryBuilderService {
  /**
   * Build querystring parameters from query object
   * @param queryString - JSON string from CustomBackendService.buildQuery()
   * @returns Object with query parameters for HTTP request
   */
  buildQueryParams(queryString: string): Record<string, any> {
    const params: Record<string, any> = {};

    try {
      const query = JSON.parse(queryString);

      if (query.columnFilters) {
        this.processFilters(query.columnFilters, params);
      }

      if (query.sortColumns && query.sortColumns.length > 0) {
        this.processSorting(query.sortColumns, params);
      }

      if (query.pagination) {
        this.processPagination(query.pagination, params);
      }
    } catch (error) {
      console.error('Error parsing query string:', error);
    }

    return params;
  }

  private processFilters(columnFilters: Record<string, any>, params: Record<string, any>): void {
    Object.keys(columnFilters).forEach(field => {
      const filterValue = columnFilters[field];

      // Skip empty values
      if (filterValue === null || filterValue === undefined || filterValue === '') {
        return;
      }

      // String filter: ?fieldName=value
      if (typeof filterValue === 'string') {
        params[field] = this.normalizeDateFilterValue(filterValue);
        return;
      }

      // Number filter: ?fieldName=value
      if (typeof filterValue === 'number') {
        params[field] = filterValue;
        return;
      }

      // Boolean filter: ?fieldName=true|false
      if (typeof filterValue === 'boolean') {
        params[field] = filterValue;
        return;
      }

      // Object-based filters (range, date, etc.)
      if (typeof filterValue === 'object') {
        this.processComplexFilter(field, filterValue, params);
      }
    });
  }

  private processComplexFilter(field: string, filterValue: any, params: Record<string, any>): void {
    // Number/Date range: flat properties for backend
    if (filterValue.from !== undefined || filterValue.to !== undefined) {
      const normalizedFrom = this.normalizeDateFilterValue(filterValue.from);
      const normalizedTo = this.normalizeDateFilterValue(filterValue.to);
      const hasFrom = normalizedFrom !== undefined && normalizedFrom !== null && normalizedFrom !== '';
      const hasTo = normalizedTo !== undefined && normalizedTo !== null && normalizedTo !== '';

      // If from == to, use equality filter instead of range
      if (hasFrom && hasTo && normalizedFrom === normalizedTo) {
        params[field] = normalizedFrom;
        return;
      }

      // Otherwise, use separate From/To properties
      if (hasFrom) {
        params[`${field}From`] = normalizedFrom;
      }

      if (hasTo) {
        params[`${field}To`] = normalizedTo;
      }

      return;
    }

    // Date filter: ?fieldName=24-1-2025
    if (filterValue.date || filterValue instanceof Date) {
      const date = filterValue.date || filterValue;
      params[field] = this.formatDate(date);
      return;
    }

    // Array of values: ?fieldName=value1,value2,value3
    if (Array.isArray(filterValue)) {
      params[field] = filterValue.join(',');
      return;
    }

    // Default: try to convert to string
    params[field] = String(filterValue);
  }

  /**
   * Process sorting into query params
   * Supports multiple sortings in format: ?sorting=name asc,code desc
   */
  private processSorting(sortColumns: any[], params: Record<string, any>): void {
    if (sortColumns.length === 0) {
      return;
    }

    const sortingParts = sortColumns
      .map(sort => {
        const field = sort.columnId || sort.field;
        const direction = sort.direction || 'ASC';
        return field ? `${field} ${direction}` : null;
      })
      .filter(part => part !== null);

    if (sortingParts.length > 0) {
      params['sorting'] = sortingParts.join(',');
    }
  }

  /**
   * Process pagination into query params
   * Format: ?skipCount=0&maxResultCount=25
   */
  private processPagination(pagination: any, params: Record<string, any>): void {
    if (pagination.pageNumber !== undefined && pagination.pageSize !== undefined) {
      // Calculate skipCount from pageNumber (pageNumber is 1-based)
      const skipCount = (pagination.pageNumber - 1) * pagination.pageSize;
      params['skipCount'] = skipCount;
      params['maxResultCount'] = pagination.pageSize;
    }
  }

  /**
   * Format date to ISO 8601 UTC format
   * Example: 2025-01-24T00:00:00.000Z
   *
   * Dates are always sent to the backend in UTC format with time component
   */
  private formatDate(date: Date | string): string {
    if (typeof date === 'string') {
      if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
        return date;
      }

      date = new Date(date);
    }

    if (!(date instanceof Date) || isNaN(date.getTime())) {
      return '';
    }

    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }

  private normalizeDateFilterValue(value: any): any {
    if (value instanceof Date) {
      return this.formatDate(value);
    }

    if (typeof value === 'string' && (/^\d{4}-\d{2}-\d{2}$/.test(value) || /^\d{4}-\d{2}-\d{2}T/.test(value))) {
      return this.formatDate(value);
    }

    return value;
  }

  /**
   * Convert params object to URL query string
   * Example: { name: 'test', age: 25 } => "name=test&age=25"
   */
  toQueryString(params: Record<string, any>): string {
    return Object.keys(params)
      .filter(key => params[key] !== undefined && params[key] !== null && params[key] !== '')
      .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
      .join('&');
  }
}
